import { useChat } from "@ai-sdk/react";
import { LogLevel } from "@eu.mdotm.api/sphere";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useQueryClient } from "@tanstack/react-query";
import { type ChatStatus, type UIMessage, getToolName, isToolUIPart } from "ai";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import { FunctionalAreasStateContext } from "$root/App/context";
import { reportPlatformError } from "$root/api/error-reporting";
import { useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { AppContextChipProvider } from "./AppContextChip";
import { buildAppContextSystemMessage, describeAppContext } from "./appContext";
import { buildMutatingToolNames, scheduleInvalidateAfterBotWrite } from "./botWriteInvalidation";
import { sphereAIChatGate, toolDiscoveryStatus } from "./chatAvailability";
import { useChatSettings } from "./chatSettings";
import { isChoiceToolName, parseChoiceSpec } from "./choices/choiceSpec";
import {
	CHOICE_CANCELLED_RESULT,
	type SphereAIChoicePrompt,
	SphereAIChoicePromptProvider,
} from "./choices/SphereAIChoice";
import {
	SYNC_INTERVAL_MS,
	conversationIsBusy,
	ownTurnState,
	settledTaskTools,
	visibleMessages,
} from "./conversationSync";
import { serverLoopStop } from "./loopGuard";
import { type SphereAIChatTool, isTaskTool, MCPHttpClient } from "./MCPHttpClient";
import { absorbServerToolResults } from "./serverToolResults";
import { conversationAnchorMs, ensureMessageTimestamps } from "./messageTimestamps";
import {
	OPEN_PAGE_TOOL_NAME,
	buildOpenPageTool,
	parseOpenPageSpec,
	portfolioLabLinkRule,
} from "./navigation/openPageSpec";
import { SphereAIRateLimitedError } from "./rateLimit";
import { sphereAiConversationsRepository } from "./repository";
import { createSphereAIChatTransport } from "./SphereAIChatTransport";
import { type SphereAILoopGuardPrompt, SphereAILoopGuardProvider } from "./SphereAILoopNotice";
import { SphereAIThread } from "./SphereAIThread";
import { getConversation } from "./serverChatStore";
import { hydrateToolResults } from "./toolResultStore";
import { useBotLabRecalculate } from "./useBotLabRecalculate";
import { useBotLabWriteResults } from "./useBotLabWriteResults";

/** The user turn Continue sends, which the model reads; not translated, like everything sent to the model. */
const CONTINUE_MESSAGE = "Continue";

/** No turn of this tab in flight: the last one finished, or its stream broke. */
function isBetweenTurns(status: ChatStatus): boolean {
	return status === "ready" || status === "error";
}

/**
 * The user message a choice answer becomes: the picked option's label (or the typed "Other" text),
 * several joined for a multi-select. `null` for a dismissal, which sends nothing.
 *
 * @internal — exported for tests
 */
export function choiceAnswerText(args: unknown, value: string): string | null {
	if (value === CHOICE_CANCELLED_RESULT) return null;
	const parsed = parseChoiceSpec(args);
	const labelOf = (v: string) => (parsed.ok ? (parsed.spec.options.find((o) => o.value === v)?.label ?? v) : v);
	if (parsed.ok && parsed.spec.multiple) {
		try {
			const values: unknown = JSON.parse(value);
			if (Array.isArray(values)) return values.map((v) => labelOf(String(v))).join(", ");
		} catch {
			// Not an array: fall through and send it as typed.
		}
	}
	return labelOf(value);
}

/**
 * The conversation view. sphere-mcp runs the whole chat (MDOT-25078): the agent loop, every tool,
 * the background tasks and their report turns, and it stores the transcript. This component renders
 * it, sends what the user types, and re-reads the stored conversation while it is still changing on
 * the server (see conversationSync.ts). The only things it still DOES are view actions: navigate for
 * `open_page`, and refresh the app's caches after the bot wrote something (botWriteInvalidation.ts).
 */
export function SphereAIChatSection({
	chatId,
	api,
	mcpUrl,
	model,
	initialDraft,
	onDraftApplied,
}: {
	chatId: string | null;
	api: string;
	mcpUrl: string;
	model: string;
	/** Text to prefill the composer with on mount (a question relaunched from History), not sent. */
	initialDraft?: string | null;
	/** Called once the draft has been applied to the composer, so the owner can clear it. */
	onDraftApplied?: () => void;
}): React.JSX.Element {
	const mcpClient = useMemo(() => new MCPHttpClient(mcpUrl), [mcpUrl]);

	// The SphereAI chat lives in the persistent AppDrawer (a sibling of the router
	// Switch), so navigating via `open_page` doesn't unmount it — the conversation stays
	// live while the page behind it changes.
	const history = useHistory();

	// Inject what the user is currently viewing (selected portfolio, regime
	// region, outlook filters, …) as hidden context on every turn. Kept in a ref
	// so the transport stays stable while always reading the latest UI state.
	const functionalAreasState = useContext(FunctionalAreasStateContext);
	const functionalAreasStateRef = useRef(functionalAreasState);
	functionalAreasStateRef.current = functionalAreasState;

	// The visible "attached context" badge lets the user toggle sharing of their
	// current screen off/on. The "off" state is keyed on the context signature, so
	// hiding it here doesn't keep it hidden after they navigate to a different context —
	// it re-enables on its own. `contextActive` gates both the badge and the hidden
	// system message; a ref keeps the transport stable while reading the latest value.
	const appContext = describeAppContext(functionalAreasState);
	const [hiddenContextSignature, setHiddenContextSignature] = useState<string | null>(null);
	const contextActive = appContext != null && appContext.signature !== hiddenContextSignature;
	const contextActiveRef = useRef(contextActive);
	contextActiveRef.current = contextActive;

	// Discovering the MCP server's tools is a PRECONDITION of the chat, not a decoration: the model can
	// only do server-side work it was handed a tool for. `listTools` is shared with `ensureTools` below
	// so the eager fetch and the send-path await are the SAME request.
	const queryClient = useQueryClient();
	// The web-access switch is part of the KEY, because it is part of the answer: the server decides
	// per request which tools that request's `tools/list` advertises, and with web search off `web-fetch`
	// is not among them (sphere-mcp's mcp/index.ts registers it conditionally). Discovery runs once per
	// mount, so without this the list stays whatever the switch said when the panel opened — turning
	// web search ON mid-conversation left the model with no web tool and it went on saying it cannot
	// reach the web until a new chat remounted the panel and re-listed. Turning it OFF never had that
	// problem: the web-fetch handler re-reads the flag per call and refuses, which is why only one
	// direction was broken. Keying on it re-lists on every flip, so the advertisement and the per-call
	// enforcement now agree in both directions.
	const { settings: chatSettings } = useChatSettings();
	const toolsQueryKey = useMemo(
		() => ["sphere-mcp-tools", mcpUrl, chatSettings.webSearch] as const,
		[mcpUrl, chatSettings.webSearch],
	);
	const listTools = useCallback(() => mcpClient.listTools(), [mcpClient]);
	// `keepPreviousData` so a flip of that switch does not blank the conversation: the key change
	// starts a fresh fetch, and without it `data` would be undefined for the round trip, which
	// `toolDiscoveryStatus` reads as "loading" and the gate renders as the panel-wide loader. Holding
	// the previous list keeps the thread on screen; the send path awaits the NEW list regardless (see
	// `ensureTools`), so no turn goes out against the superseded one.
	const toolsQuery = useQueryNoRefetch(toolsQueryKey, { queryFn: listTools, keepPreviousData: true });

	// Tool discovery is the precondition, so its failure is a HARD failure: the chat is replaced by the
	// unavailability wall rather than opened with a model that has nothing to work with (see
	// chatAvailability.ts for what that "useless chat" looked like, and why an empty list counts too).
	const toolsStatus = toolDiscoveryStatus(toolsQuery);

	// A 429 on discovery is the one failure that says when to come back; every other one carries no
	// delay. Same rule as the transcript read below, and the wall says the wait for both.
	const [toolsRetryAfterMs, setToolsRetryAfterMs] = useState<number | undefined>(undefined);
	const retryToolDiscovery = useCallback(async () => {
		await queryClient.refetchQueries({ queryKey: toolsQueryKey });
	}, [queryClient, toolsQueryKey]);
	useQueryReactor(toolsQuery, {
		onFetching() {
			// A retry is in flight — the previous verdict, delay included, is no longer the answer.
			setToolsRetryAfterMs(undefined);
		},
		onError(error) {
			setToolsRetryAfterMs(error instanceof SphereAIRateLimitedError ? error.retryAfterMs : undefined);
			reportPlatformError(
				error instanceof Error ? error : new Error(`Sphere AI could not discover its MCP tools: ${error}`),
				LogLevel.Error,
				"ask-intelligence",
			);
		},
		onSuccess(tools) {
			setToolsRetryAfterMs(undefined);
			// A 200 that advertises nothing is reported too: it walls the panel exactly like a failed
			// request does, and nothing else in the stack would notice a server with an empty registry.
			if (tools.length === 0) {
				reportPlatformError(new Error("Sphere AI advertised no MCP tools"), LogLevel.Error, "ask-intelligence");
			}
		},
	});

	// The transport awaits discovery before every send: a turn must not go out while the assistant's
	// availability is unknown (the gate walls the panel if discovery failed).
	const ensureTools = useCallback(
		() => queryClient.ensureQueryData({ queryKey: toolsQueryKey, queryFn: listTools }),
		[queryClient, toolsQueryKey, listTools],
	);
	const ensureToolsRef = useRef(ensureTools);
	ensureToolsRef.current = ensureTools;

	// Names of MUTATING tools (task tools, anything not marked read-only, anything
	// destructive-hinted). A bot write is known FIRST here — the tool call returns in this tab once
	// the mutation has landed — so the app's caches are refreshed from the tool return rather than
	// from an SSE topic which, for several of the families the bot writes to, does not exist. See
	// botWriteInvalidation.ts.
	const mutatingToolNames = useMemo(() => buildMutatingToolNames(toolsQuery.data), [toolsQuery.data]);
	const mutatingToolNamesRef = useRef(mutatingToolNames);
	mutatingToolNamesRef.current = mutatingToolNames;

	// Background task tools write when they SETTLE, minutes after the call; their refresh runs then
	// (conversationSync's `settledTaskTools`), not at dispatch.
	const taskToolNames = useMemo(
		() => new Set((toolsQuery.data ?? []).filter((tool) => isTaskTool(tool)).map((tool) => tool.name)),
		[toolsQuery.data],
	);
	const taskToolNamesRef = useRef(taskToolNames);
	taskToolNamesRef.current = taskToolNames;

	// A mutating tool call ran: queue the post-write cache refresh. `input` is the call's arguments,
	// which the refresh mines for the uuids of the entities the bot touched, so a screen that can't be
	// refreshed by an invalidation alone can tell whether it was one of them. Omitted for a background
	// task, which no longer has its input by settle time.
	const markBotWrite = useCallback((toolName: string, input?: unknown) => {
		if (!mutatingToolNamesRef.current.has(toolName)) return;
		scheduleInvalidateAfterBotWrite(toolName, input);
	}, []);

	// One-shot hidden advisory for the turn after the user resumes a run the server's loop guard
	// parked, so "continue" does not just replay the step that tripped it. Sent once, then forgotten.
	const turnAdvisoryRef = useRef<string | null>(null);

	const transport = useMemo(
		() =>
			createSphereAIChatTransport({
				baseUrl: api,
				model,
				getTools: async (): Promise<SphereAIChatTool[]> => {
					await ensureToolsRef.current();
					// Built per request: the pages it advertises are the ones this user's services can reach.
					return [buildOpenPageTool()];
				},
				getContextMessage: () => {
					const advisory = turnAdvisoryRef.current;
					turnAdvisoryRef.current = null;
					const appContextMessage = contextActiveRef.current
						? buildAppContextSystemMessage(functionalAreasStateRef.current)
						: null;
					// The Lab link rule is an instruction, not screen data, so it rides even when the user
					// has hidden their page context.
					return (
						[appContextMessage, portfolioLabLinkRule(), advisory]
							.filter((part) => part != null && part.length > 0)
							.join("\n\n") || null
					);
				},
			}),
		[api, model],
	);

	const chat = useChat({
		transport,
		id: chatId ?? "new",
		// Fires for every tool call in the stream; sphere-mcp has already executed it (or is about to),
		// so nothing here answers it. Two view actions only.
		onToolCall({ toolCall }) {
			if (toolCall.toolName === OPEN_PAGE_TOOL_NAME) {
				// Only in the live tab: a reloaded conversation never navigates.
				const parsed = parseOpenPageSpec(toolCall.input);
				if (parsed.ok) history.push(parsed.url);
				return;
			}
			if (!taskToolNamesRef.current.has(toolCall.toolName)) markBotWrite(toolCall.toolName, toolCall.input);
		},
		onError(err) {
			reportPlatformError(err, LogLevel.Error, "ask-intelligence");
		},
		onFinish() {
			// The server stored the turn; the history list may now hold a new chat or a new title.
			sphereAiConversationsRepository.invalidateList();
		},
	});

	const { messages: rawMessages, status, error, setMessages } = chat;
	const statusRef = useRef(status);
	statusRef.current = status;
	// Moves on every render a turn of this tab is in flight, so a re-read dispatched before the turn
	// can tell, when it lands, that its snapshot predates the turn.
	const syncGenerationRef = useRef(0);
	if (!isBetweenTurns(status)) syncGenerationRef.current += 1;
	const sendMessageRef = useRef(chat.sendMessage);
	sendMessageRef.current = chat.sendMessage;
	const clearErrorRef = useRef(chat.clearError);
	clearErrorRef.current = chat.clearError;

	// Fill `toolResultStore` (links) from the tool results in the transcript, live or restored
	// alike — see serverToolResults.ts.
	useEffect(() => {
		absorbServerToolResults(rawMessages);
	}, [rawMessages]);

	useBotLabWriteResults({ messages: rawMessages, status, mutatingToolNames, taskToolNames });
	useBotLabRecalculate({ messages: rawMessages, status });

	// The open question, if any: a `radio`/`select` call in the newest message, which the server
	// answered "on screen" and ended the turn on. Its picker takes over the composer; the answer is
	// sent as the user's next message. Dismissing it just gives the composer back.
	const [dismissedChoice, setDismissedChoice] = useState<string | null>(null);
	const choicePrompt = useMemo<SphereAIChoicePrompt | null>(() => {
		const last = rawMessages.at(-1);
		if (status !== "ready" || last?.role !== "assistant") return null;
		for (const part of last.parts) {
			if (!isToolUIPart(part) || part.state !== "output-available") continue;
			const toolName = getToolName(part);
			if (!isChoiceToolName(toolName) || part.toolCallId === dismissedChoice) continue;
			const { toolCallId, input } = part;
			return {
				toolCallId,
				toolName,
				args: input,
				addResult: (value: string) => {
					const text = choiceAnswerText(input, value);
					if (text === null) {
						setDismissedChoice(toolCallId);
						return;
					}
					void sendMessageRef.current({ text }).catch(noop);
				},
			};
		}
		return null;
	}, [rawMessages, status, dismissedChoice]);

	// The run sphere-mcp's loop guard stopped, if any: Continue resumes it with a one-shot advisory
	// (a "Continue" message), Stop leaves it parked.
	const loopStop = useMemo(() => (status === "ready" ? serverLoopStop(rawMessages) : null), [status, rawMessages]);
	const [dismissedLoopStop, setDismissedLoopStop] = useState<string | null>(null);
	const reportedLoopStopRef = useRef<string | null>(null);
	useEffect(() => {
		// The step ceiling is a designed stop, not a fault: only the loop guard's are reported.
		if (!loopStop || loopStop.reason === "max-turns" || reportedLoopStopRef.current === loopStop.runId) return;
		reportedLoopStopRef.current = loopStop.runId;
		reportPlatformError(
			new Error(`Sphere AI loop guard stopped a run (chat ${chatId ?? "new"}): ${loopStop.summary}`),
			LogLevel.Warn,
			"ask-intelligence",
		);
	}, [loopStop, chatId]);
	const loopGuardPrompt = useMemo<SphereAILoopGuardPrompt | null>(() => {
		if (!loopStop || loopStop.runId === dismissedLoopStop) return null;
		return {
			summary: loopStop.summary,
			onContinue: () => {
				setDismissedLoopStop(loopStop.runId);
				turnAdvisoryRef.current = loopStop.advisory;
				// sphere-mcp stores every turn as its own assistant message, so Continue sends a user
				// message: `sendMessage()` with none would append the turn to the stopped answer instead.
				if (statusRef.current === "ready") sendMessageRef.current({ text: CONTINUE_MESSAGE }).catch(noop);
			},
			onStop: () => setDismissedLoopStop(loopStop.runId),
		};
	}, [loopStop, dismissedLoopStop]);

	// Whether the stored conversation, as last read, is still changing on the server.
	const [serverBusy, setServerBusy] = useState(false);

	// The one timestamp anchor for this conversation, captured at restore and reused by the poll so
	// an un-stamped message is backfilled to the SAME value every tick (see conversationAnchorMs).
	const anchorMsRef = useRef<number | null>(null);
	// The last RAW server transcript applied, kept so the poll compares against server data alone —
	// never against the client-backfilled copy, whose invented timestamps would differ every tick.
	const lastServerMessagesRef = useRef<UIMessage[] | null>(null);
	// A switched-to conversation must never reuse the previous one's anchor or last-seen messages.
	// biome-ignore lint/correctness/useExhaustiveDependencies: chatId is the sole trigger; the body reads nothing.
	useEffect(() => {
		anchorMsRef.current = null;
		lastServerMessagesRef.current = null;
	}, [chatId]);

	const chatDataQuery = useQueryNoRefetch(["retrieveSelectedChatAIData", chatId], {
		queryFn: async () => {
			if (!chatId)
				return {
					remappedMessages: [] as UIMessage[],
					anchorMs: Date.now(),
					serverMessages: [] as UIMessage[],
					toolResults: undefined,
				};
			// `null` is a conversation with nothing stored yet.
			const stored = await getConversation(chatId);
			// Backfill per-message timestamps so older conversations render an auditable time, anchored
			// to the conversation's own activity rather than the current wall clock.
			const anchorMs = conversationAnchorMs(stored?.lastMessageTime, stored?.creationTime);
			const serverMessages = stored?.messages ?? [];
			return {
				remappedMessages: ensureMessageTimestamps(serverMessages, anchorMs),
				anchorMs,
				serverMessages,
				toolResults: stored?.toolResults,
			};
		},
	});

	// Tracks the chatId whose persisted messages have been applied via setMessages.
	// The Thread is gated on this: when chatId changes (e.g. a fresh chat being promoted
	// to a real UUID in onFinish, which remounts with a new useChat id and momentarily
	// empty messages), the styled Thread would otherwise flash its centered "welcome"
	// screen before the reload lands. Holding the Thread back until the restore for the
	// current chatId is applied removes that flicker.
	const [restoredChatId, setRestoredChatId] = useState<string | null | undefined>(undefined);

	// The same marker for the FAILED restore. Kept as a chatId rather than a boolean for the reason
	// `restoredChatId` is: both are answers about one specific conversation, and a chatId change
	// (opening another transcript) must invalidate the previous answer rather than
	// carry a stale error onto the new chat.
	const [restoreErrorChatId, setRestoreErrorChatId] = useState<string | null | undefined>(undefined);

	// The server's own `Retry-After` when the failure was a 429, in ms. Undefined for every other
	// failure — and also for a 429 whose header never made it across the origin boundary.
	const [restoreRetryAfterMs, setRestoreRetryAfterMs] = useState<number | undefined>(undefined);

	// Retry for the fallback below. Read through a ref for the usual reason: the query object is a new
	// identity every render, and the callback is handed to a child that shouldn't re-render for it.
	const chatDataQueryRef = useRef(chatDataQuery);
	chatDataQueryRef.current = chatDataQuery;
	const retryRestore = useCallback(async () => {
		await chatDataQueryRef.current.refetch();
	}, []);

	// Three outcomes, not two. Anything that is neither restored nor failed is still in flight —
	// including the retries React Query runs before it calls a read failed.
	const restoreStatus: "loading" | "ready" | "error" =
		restoredChatId === chatId ? "ready" : restoreErrorChatId === chatId ? "error" : "loading";

	useQueryReactor(chatDataQuery, {
		onFetching() {
			setRestoreErrorChatId(undefined);
			setRestoreRetryAfterMs(undefined);
		},
		onError(error) {
			// No messages were applied, so the transcript is not "restored" — the fallback shows instead.
			setRestoreErrorChatId(chatId);
			setRestoreRetryAfterMs(error instanceof SphereAIRateLimitedError ? error.retryAfterMs : undefined);
			reportPlatformError(
				error instanceof Error ? error : new Error(`Sphere AI could not restore chat ${chatId ?? "new"}: ${error}`),
				LogLevel.Error,
				"ask-intelligence",
			);
		},
		onSuccess({ remappedMessages, anchorMs, serverMessages, toolResults }) {
			// A conversation stored before MDOT-25078 holds its tool output as text, with the links in
			// this map; in before the messages, so the first render resolves them.
			hydrateToolResults(toolResults);
			anchorMsRef.current = anchorMs;
			lastServerMessagesRef.current = serverMessages;
			setServerBusy(conversationIsBusy(serverMessages));
			setMessages(visibleMessages(remappedMessages));
			setRestoredChatId(chatId);
		},
	});

	// A stream that broke may have lost only the connection: the server runs every turn to the end
	// and stores it. So a transport failure (fetch and stream reads reject with a TypeError) re-reads
	// the conversation, and each read places THIS tab's turn (ownTurnState, conversationSync.ts).
	// Still running: the reads go on and the partial answer stays on screen. Answered: the stored
	// conversation replaces the screen and the error is cleared. Anything else — a request turned
	// away before it was stored, a turn that died leaving only its empty placeholder — leaves the
	// screen and the error as they are. An error the server streamed is its own verdict on the turn:
	// its text is the explanation, so it is never read back over.
	const [recovering, setRecovering] = useState(false);
	const recoveringRef = useRef(false);
	recoveringRef.current = recovering;
	useEffect(() => {
		if (status === "error") setRecovering(error instanceof TypeError);
		else if (status !== "ready") setRecovering(false); // a new turn: its own stream is the source again
	}, [status, error]);

	// Re-read the stored conversation while it is still changing on the server — a background task
	// running, its report turn, a turn that was running when this tab loaded, or one whose stream
	// broke. Only between turns of this tab: while one streams, the stream is the source.
	// Busy is the STORED conversation's verdict (its running placeholders are not rendered) or the
	// live one's (a task this tab just launched). After an error only the recovery reads: the live
	// messages are the broken turn's, and the stored verdict waits for this tab's next turn.
	const inFlightRef = useRef(false);
	const lastUserMessageIdRef = useRef<string | undefined>(undefined);
	lastUserMessageIdRef.current = rawMessages.findLast((message) => message.role === "user")?.id;
	const busy =
		restoredChatId === chatId &&
		(status === "ready" ? serverBusy || conversationIsBusy(rawMessages) : status === "error" && recovering);
	useEffect(() => {
		if (!busy || !chatId) return;
		const timer = setInterval(() => {
			// One read at a time: a slow one would otherwise race the next to setMessages.
			if (inFlightRef.current) return;
			inFlightRef.current = true;
			const generation = syncGenerationRef.current;
			void getConversation(chatId)
				.then((stored) => {
					if (!isBetweenTurns(statusRef.current) || generation !== syncGenerationRef.current) return;
					const ownTurn =
						stored && recoveringRef.current ? ownTurnState(stored.messages, lastUserMessageIdRef.current) : null;
					if (recoveringRef.current && ownTurn !== "running") {
						recoveringRef.current = false;
						setRecovering(false);
					}
					setServerBusy(stored ? conversationIsBusy(stored.messages) : false);
					// After an error only this tab's own turn is read back: what is on screen may not be
					// stored, so anything else waits for the next turn.
					if (!stored || (statusRef.current === "error" && ownTurn === null)) return;
					// Its placeholder is not rendered, so applying it would blank the partial answer.
					if (ownTurn === "running") return;
					const beforeServer = lastServerMessagesRef.current;
					// Server data only: the client-backfilled timestamps must not make this fire on an
					// unchanged poll.
					if (beforeServer && JSON.stringify(stored.messages) === JSON.stringify(beforeServer)) return;
					const anchorMs = anchorMsRef.current ?? conversationAnchorMs(stored.lastMessageTime, stored.creationTime);
					anchorMsRef.current = anchorMs;
					const next = visibleMessages(ensureMessageTimestamps(stored.messages, anchorMs));
					for (const toolName of settledTaskTools(beforeServer ?? [], stored.messages)) markBotWrite(toolName);
					lastServerMessagesRef.current = stored.messages;
					setMessages(next);
					sphereAiConversationsRepository.invalidateList();
					if (ownTurn === "answered") clearErrorRef.current();
				})
				.catch(noop)
				.finally(() => {
					inFlightRef.current = false;
				});
		}, SYNC_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [busy, chatId, setMessages, markBotWrite]);

	// One verdict for the Thread, folded out of the two preconditions — the transcript read and MCP
	// tool discovery — with the wall's copy keyed on which of them failed. See chatAvailability.ts.
	const gate = sphereAIChatGate({
		tools: {
			status: toolsStatus,
			retryAfterMs: toolsRetryAfterMs,
			onRetry: retryToolDiscovery,
		},
		conversation: {
			status: restoreStatus,
			retryAfterMs: restoreRetryAfterMs,
			onRetry: retryRestore,
		},
	});

	// Hold the Thread until this chatId's messages are restored and the tools are known, so the
	// promotion remount (empty useChat) doesn't flash the welcome screen — and hand it the FAILED
	// cases too, so an unreachable store or an assistant with no tools ends in a retryable wall
	// instead of a loader that spins for the life of the panel, or a chat that cannot do anything.
	return (
		<AppContextChipProvider
			value={{
				label: appContext?.label ?? null,
				active: contextActive,
				// Toggle: hide by keying on the current signature; re-enable by clearing it.
				toggle: () => setHiddenContextSignature(contextActive ? (appContext?.signature ?? null) : null),
			}}
		>
			<SphereAILoopGuardProvider value={loopGuardPrompt}>
				<SphereAIChoicePromptProvider value={choicePrompt}>
					<SphereAIThread chat={chat} gate={gate} initialDraft={initialDraft} onDraftApplied={onDraftApplied} />
				</SphereAIChoicePromptProvider>
			</SphereAILoopGuardProvider>
		</AppContextChipProvider>
	);
}
